// ScientificNamingCalculator.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ScientificNamingCalculator = () => {
  const [nameData, setNameData] = useState({
    commonName: '',
    genus: '',
    species: '',
    subspecies: '',
    author: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateScientificName = () => {
    const { commonName, genus, species, subspecies, author } = nameData;
    
    if (!commonName.trim() || !genus.trim() || !species.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate scientific name components
    const scientificName = {
      binomial: `${genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase()} ${species.toLowerCase()}`,
      trinomial: subspecies ? `${genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase()} ${species.toLowerCase()} ${subspecies.toLowerCase()}` : null,
      full: subspecies ? `${genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase()} ${species.toLowerCase()} ${subspecies.toLowerCase()} ${author ? `(${author})` : ''}` : null
    };

    // Generate taxonomic classification
    const classification = {
      kingdom: 'Animalia',
      phylum: 'Chordata',
      class: 'Mammalia',
      order: 'Primates',
      family: 'Hominidae',
      genus: genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase(),
      species: species.toLowerCase()
    };

    // Generate naming rules
    const namingRules = {
      binomial: 'Genus species (italicized, genus capitalized)',
      trinomial: 'Genus species subspecies (italicized, genus capitalized)',
      author: 'Author name in parentheses after the scientific name'
    };

    // Generate example usage
    const exampleUsage = {
      scientific: `The ${commonName} is scientifically known as ${scientificName.binomial}.`,
      classification: `It belongs to the family ${classification.family} and genus ${classification.genus}.`,
      full: scientificName.full ? `The complete scientific name is ${scientificName.full}.` : ''
    };

    // Generate calculation steps
    const calculationSteps = [
      'Step 1: Prepare the name components',
      `Common name: ${commonName}`,
      `Genus: ${genus}`,
      `Species: ${species}`,
      `Subspecies: ${subspecies || 'None'}`,
      `Author: ${author || 'None'}`,
      '\nStep 2: Generate scientific name',
      `Binomial: ${genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase()} ${species.toLowerCase()}`,
      subspecies ? `Trinomial: ${genus.charAt(0).toUpperCase() + genus.slice(1).toLowerCase()} ${species.toLowerCase()} ${subspecies.toLowerCase()}` : '',
      author ? `Author: (${author})` : '',
      '\nStep 3: Generate classification',
      `Kingdom: ${classification.kingdom}`,
      `Phylum: ${classification.phylum}`,
      `Class: ${classification.class}`,
      `Order: ${classification.order}`,
      `Family: ${classification.family}`,
      `Genus: ${classification.genus}`,
      `Species: ${classification.species}`
    ];

    // Generate chart data
    const chartData = {
      bar: {
        labels: ['Kingdom', 'Phylum', 'Class', 'Order', 'Family', 'Genus', 'Species'],
        datasets: [{
          label: 'Classification Levels',
          data: [1, 2, 3, 4, 5, 6, 7],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)',
            'rgba(201, 203, 207, 0.5)',
            'rgba(75, 192, 192, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(201, 203, 207, 1)',
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Genus', 'Species', 'Subspecies'],
        datasets: [{
          data: [1, 1, subspecies ? 1 : 0],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 159, 64, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      }
    };

    const newResults = {
      scientificName,
      classification,
      namingRules,
      exampleUsage,
      calculationSteps
    };

    setResults(newResults);
    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Scientific Naming Calculator | Generate Scientific Names</title>
        <meta name="description" content="Generate scientific names for organisms with our Scientific Naming Calculator. Get accurate binomial and trinomial nomenclature." />
        <meta name="keywords" content="scientific naming, binomial nomenclature, trinomial nomenclature, taxonomic classification, biological nomenclature" />
        <meta property="og:title" content="Scientific Naming Calculator | Generate Scientific Names" />
        <meta property="og:description" content="Generate scientific names for organisms with our Scientific Naming Calculator. Get accurate binomial and trinomial nomenclature." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/scientific-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Scientific Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate Scientific Names for Organisms</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you generate scientific names for organisms using binomial and trinomial nomenclature. It also provides taxonomic classification and naming rules.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="commonName">
            Common Name:
          </label>
          <input
            type="text"
            id="commonName"
            name="commonName"
            value={nameData.commonName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the common name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="genus">
            Genus:
          </label>
          <input
            type="text"
            id="genus"
            name="genus"
            value={nameData.genus}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the genus name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="species">
            Species:
          </label>
          <input
            type="text"
            id="species"
            name="species"
            value={nameData.species}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the species name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subspecies">
            Subspecies (optional):
          </label>
          <input
            type="text"
            id="subspecies"
            name="subspecies"
            value={nameData.subspecies}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the subspecies name (if applicable)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="author">
            Author (optional):
          </label>
          <input
            type="text"
            id="author"
            name="author"
            value={nameData.author}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the author's name (if applicable)"
          />
        </div>

        <button
          onClick={generateScientificName}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Scientific Name
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Scientific Name</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Binomial Name</span>
                    <span className="font-medium">{results.scientificName.binomial}</span>
                  </p>
                  {results.scientificName.trinomial && (
                    <p className="flex justify-between">
                      <span>Trinomial Name</span>
                      <span className="font-medium">{results.scientificName.trinomial}</span>
                    </p>
                  )}
                  {results.scientificName.full && (
                    <p className="flex justify-between">
                      <span>Full Name</span>
                      <span className="font-medium">{results.scientificName.full}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Taxonomic Classification</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Kingdom</span>
                    <span className="font-medium">{results.classification.kingdom}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Phylum</span>
                    <span className="font-medium">{results.classification.phylum}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Class</span>
                    <span className="font-medium">{results.classification.class}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Order</span>
                    <span className="font-medium">{results.classification.order}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Family</span>
                    <span className="font-medium">{results.classification.family}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Genus</span>
                    <span className="font-medium">{results.classification.genus}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Species</span>
                    <span className="font-medium">{results.classification.species}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Naming Rules</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Binomial</span>
                    <span className="font-medium">{results.namingRules.binomial}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Trinomial</span>
                    <span className="font-medium">{results.namingRules.trinomial}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Author</span>
                    <span className="font-medium">{results.namingRules.author}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Scientific Name</span>
                    <span className="font-medium">{results.exampleUsage.scientific}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Classification</span>
                    <span className="font-medium">{results.exampleUsage.classification}</span>
                  </p>
                  {results.exampleUsage.full && (
                    <p className="flex justify-between">
                      <span>Full Name</span>
                      <span className="font-medium">{results.exampleUsage.full}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Classification Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Classification Level'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Taxonomic Rank'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Taxonomic Classification Levels'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Nomenclature Chart</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Nomenclature Components'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Scientific Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Scientific Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Organism Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the common name of the organism</li>
                <li>Input the genus name (required)</li>
                <li>Input the species name (required)</li>
                <li>Optionally add subspecies name if applicable</li>
                <li>Optionally add author name if applicable</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Scientific Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Scientific Name" button</li>
                <li>Review the generated scientific name</li>
                <li>Check the taxonomic classification</li>
                <li>Verify the naming rules</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the binomial and trinomial names</li>
                <li>Review the taxonomic classification</li>
                <li>Verify the naming rules</li>
                <li>Use the example usage for reference</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      
      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is scientific naming?</h3>
              <p className="text-gray-700">
                Scientific naming, or binomial nomenclature, is a system used to name species of living organisms. It was developed by Carl Linnaeus and uses a two-part name: the genus and species.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What's the difference between binomial and trinomial nomenclature?</h3>
              <p className="text-gray-700">
                Binomial nomenclature uses two parts (genus and species), while trinomial nomenclature adds a third part for subspecies designation. For example:
                <br/><br/>
                Binomial: <i>Homo sapiens</i>
                <br/>
                Trinomial: <i>Homo sapiens sapiens</i>
              </p>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Why are scientific names important?</h3>
              <p className="text-gray-700">
                Scientific names provide a standardized way to identify species across languages and cultures. They help prevent confusion and ensure clear communication in scientific research.
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">How do I format scientific names correctly?</h3>
              <p className="text-gray-700">
                Scientific names should be italicized in print, with the genus name capitalized and the species name in lowercase. For example: <i>Homo sapiens</i>
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is the taxonomic hierarchy?</h3>
              <p className="text-gray-700">
                The taxonomic hierarchy, from broadest to most specific, is: Kingdom → Phylum → Class → Order → Family → Genus → Species.
              </p>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Can I use this calculator for any organism?</h3>
              <p className="text-gray-700">
                Yes, this calculator can be used for any organism. The taxonomic classification system is universal and applies to all living organisms.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Generate both binomial and trinomial scientific names</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Complete taxonomic classification system</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Support for author attribution</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Interactive charts and visualizations</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Step-by-step calculation process</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Save time with automated name generation</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Ensure accurate scientific naming conventions</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Visualize taxonomic relationships</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Learn naming rules through examples</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Perfect for students and researchers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Scientific Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Scientific naming, or binomial nomenclature, is a system of naming species of living organisms. It was developed by Carl Linnaeus in the 18th century and is still used today.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Binomial Nomenclature:</span> Uses two parts - genus and species
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Trinomial Nomenclature:</span> Includes subspecies designation
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Taxonomic Classification:</span> Organizes organisms into hierarchical groups
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Naming Rules:</span> Follows strict conventions for consistency
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Scientific names are always italicized in print, with the genus name capitalized and the species name in lowercase.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScientificNamingCalculator;
